import {
  type SortDirection,
  compareNullsLast,
  sortFactor,
} from "~/pages/manifold-nextgen/components/evals/TableSort";
import {} from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import { RunSortKey } from "~/pages/manifold-nextgen/components/evals/constants/sorting";
import type { EvalRunListItem, PartialEvalCaseResult, PartialEvalRun } from "~/services/evals";
import { type ResultsByRun, caseMeanScores, parseApiTimestamp, runDurationSeconds } from "./core";
import { mean } from "./math";

/** A run as the runs tab reads it: one row, one trend point, one distribution. */
export interface RunSummary {
  /** Result rows, i.e. cases x iterations — not distinct cases. */
  resultCount: number;
  distinctCaseCount: number;
  passed: number;
  failed: number;
  errored: number;
  /** Judged results only: a `passed: null` result had nothing to judge. */
  judged: number;
  /** `passed / judged`, or null when the run judged nothing. */
  passRate: number | null;
  /**
   * Mean of the run's *per-case* scores, collapsed to the case before averaging so
   * a change in iteration count cannot move the figure on its own.
   */
  meanScore: number | null;
}
export function summarizeRun(results: PartialEvalCaseResult[]): RunSummary {
  const judged = results.filter((result) => result.passed != null);
  const passed = judged.filter((result) => result.passed).length;
  const errored = results.filter((result) => result.error != null).length;

  return {
    resultCount: results.length,
    distinctCaseCount: new Set(results.map((result) => result.case_id)).size,
    passed,
    failed: judged.length - passed,
    errored,
    judged: judged.length,
    passRate: judged.length > 0 ? passed / judged.length : null,
    meanScore: mean(caseMeanScores(results)),
  };
}
export interface TrendPoint {
  runId: string;
  startedAt: string;
  /** Percent, 0-100. Runs that judged nothing are excluded, never plotted as 0. */
  passRatePercent: number;
  /** Mean of the run's case scores on the 1-4 rubric. */
  meanScore: number | null;
  /**
   * Lowest and highest *per-case* mean score in the run, drawn as the spread band.
   * Per case, so one unlucky iteration cannot stretch it to the floor.
   */
  minCaseScore: number | null;
  maxCaseScore: number | null;
  judged: number;
}
/**
 * The score series, oldest first, with the per-run spread of case scores. A run
 * that judged nothing is left out rather than plotted as zero, which would invent
 * a regression. The spread is what separates a run where every case scored 3.2
 * from one where half scored 4 and half 2.4.
 */
export function buildTrend(runs: PartialEvalRun[], resultsByRun: ResultsByRun): TrendPoint[] {
  return runs.flatMap((run) => {
    const results = resultsByRun[run.id] ?? [];
    const summary = summarizeRun(results);
    if (summary.passRate == null) {
      return [];
    }
    const caseScores = caseMeanScores(results);
    return [
      {
        runId: run.id,
        startedAt: run.started_at,
        passRatePercent: summary.passRate * 100,
        meanScore: summary.meanScore,
        minCaseScore: caseScores.length > 0 ? Math.min(...caseScores) : null,
        maxCaseScore: caseScores.length > 0 ? Math.max(...caseScores) : null,
        judged: summary.judged,
      },
    ];
  });
}
export interface RunTimePoint {
  runId: string;
  startedAt: string;
  /** Wall clock from start to completion, as the axis reads it. */
  durationMinutes: number;
}
/**
 * The run-time series, in the order the runs arrive. A run with no measurable
 * duration is left out rather than plotted as zero, which would draw a run that
 * finished instantly — see `runDurationSeconds` for what counts as unmeasurable.
 */
export function buildRunTimeTrend(runs: PartialEvalRun[]): RunTimePoint[] {
  return runs.flatMap((run) => {
    const seconds = runDurationSeconds(run);
    if (seconds == null) {
      return [];
    }
    return [{ runId: run.id, startedAt: run.started_at, durationMinutes: seconds / 60 }];
  });
}
export interface RunTableRow {
  run: EvalRunListItem;
  /** Null for a run outside the measured window, which judged nothing is not. */
  summary: RunSummary | null;
  durationSeconds: number | null;
}
/**
 * Joins each run with its summary so sorting and rendering read one shape. A run
 * `resultsByRun` has no entry for was never measured; an empty entry was.
 */
export function buildRunRows(runs: EvalRunListItem[], resultsByRun: ResultsByRun): RunTableRow[] {
  return runs.map((run) => {
    const results = resultsByRun[run.id];
    return {
      run,
      summary: results ? summarizeRun(results) : null,
      durationSeconds: runDurationSeconds(run),
    };
  });
}
/** Row order for the runs table. Nulls last, for the reason `sortCaseRows` gives. */
export function sortRunRows(
  rows: RunTableRow[],
  key: RunSortKey,
  direction: SortDirection
): RunTableRow[] {
  const factor = sortFactor(direction);
  return [...rows].sort((a, b) => {
    switch (key) {
      case RunSortKey.STARTED:
        return factor * (parseApiTimestamp(a.run.started_at) - parseApiTimestamp(b.run.started_at));
      case RunSortKey.MEAN_SCORE:
        return compareNullsLast(a.summary?.meanScore ?? null, b.summary?.meanScore ?? null, factor);
      case RunSortKey.CASES:
        return compareNullsLast(
          a.summary?.distinctCaseCount ?? null,
          b.summary?.distinctCaseCount ?? null,
          factor
        );
      case RunSortKey.DURATION:
        return compareNullsLast(a.durationSeconds, b.durationSeconds, factor);
      case RunSortKey.STATUS:
      default:
        return factor * a.run.status.localeCompare(b.run.status);
    }
  });
}

/** The key a run with no recorded judge config groups under. A run is here only
 * while in-flight or from before configs were recorded — the runner writes the
 * effective config on completion, so a finished run always has one. */
export const UNCONFIGURED_CONFIG_KEY = "__unconfigured__";

/**
 * A run's judge configuration as an order-independent key, so two runs judged
 * the same group together no matter how their `judge_config` keys are ordered.
 */
export function runConfigKey(run: Pick<EvalRunListItem, "judge_config">): string {
  const config = run.judge_config;
  if (!config || Object.keys(config).length === 0) {
    return UNCONFIGURED_CONFIG_KEY;
  }
  return JSON.stringify(
    Object.keys(config)
      .sort()
      .map((key) => [key, config[key]])
  );
}

export interface RunConfigGroup {
  key: string;
  /** 1-based label for the dropdown, assigned oldest configuration first. */
  index: number;
  judgeConfig: Record<string, string>;
}

/**
 * The distinct judge configurations across the given runs, each with a stable
 * integer label, so the dashboard can filter runs to a single comparable
 * configuration. Runs with no recorded config are counted, not grouped — they
 * cannot be compared to a finished run's effective config yet.
 */
export function groupRunsByConfig(runs: EvalRunListItem[]): {
  groups: RunConfigGroup[];
  unconfiguredCount: number;
} {
  // Oldest-first so a configuration's label does not shuffle as newer runs
  // arrive: the first run to use it fixes its number.
  const ordered = [...runs].sort(
    (a, b) => parseApiTimestamp(a.started_at) - parseApiTimestamp(b.started_at)
  );
  const groups = new Map<string, RunConfigGroup>();
  let unconfiguredCount = 0;
  for (const run of ordered) {
    const key = runConfigKey(run);
    if (key === UNCONFIGURED_CONFIG_KEY) {
      unconfiguredCount += 1;
      continue;
    }
    if (!groups.has(key)) {
      groups.set(key, { key, index: groups.size + 1, judgeConfig: run.judge_config ?? {} });
    }
  }
  return { groups: [...groups.values()], unconfiguredCount };
}
